import type {ResultadoDeRastreo} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {archivosDelRastreador, rutaListaDelRastreador, useCargarRastreador} from '../api/rastreador';
import {tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {ElegirArchivo} from './elegir-archivo';

// Actualizar el seguimiento con el rastreador de navieras (decision 0034). El
// rastreador corre en un computador con Dropbox; aqui se suben sus PDF (y su
// Excel). Primero se revisa; nada se guarda hasta confirmar.
export function ActualizarDesdeNavieras({onCerrar}: {onCerrar: () => void}) {
  const cargar = useCargarRastreador();
  const [archivos, setArchivos] = useState<File[]>([]);
  const [descartados, setDescartados] = useState(0);
  const [resultado, setResultado] = useState<ResultadoDeRastreo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enviar = async (modo: 'revisar' | 'aplicar', elegidos = archivos) => {
    if (!elegidos.length) return;
    setError(null);
    try {
      setResultado(await cargar.mutateAsync({archivos: elegidos, modo}));
    } catch (e) {
      setError(
        e instanceof ErrorDeApi ? e.message : tr('No se pudieron leer los archivos.', 'Could not read the files.'),
      );
    }
  };
  const r = resultado;
  const porGuardar = r ? r.exportaciones.filter(x => x.cambios.length || x.estadoNuevo).length : 0;

  return (
    <section
      aria-label={tr('Actualizar desde navieras', 'Update from shipping lines')}
      className="space-y-3 rounded-lg border border-verde/40 bg-white p-4 text-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">{tr('Actualizar desde navieras', 'Update from shipping lines')}</h2>
          <ol className="mt-1 list-decimal space-y-1 pl-5 text-apagado">
            <li>
              {tr(
                'En el computador con Dropbox, corre el rastreador (Run_Tracker.bat de la carpeta shipment_tracker). Para que revise las exportaciones activas de la app, descarga primero la lista y guárdala en esa carpeta como Shipment_output.xlsx: ',
                'On the computer with Dropbox, run the tracker (Run_Tracker.bat in the shipment_tracker folder). For it to check the active exports of the app, first download the list and save it in that folder as Shipment_output.xlsx: ',
              )}
              <a href={rutaListaDelRastreador} className="font-medium text-verde hover:underline">
                {tr('⬇ Lista de BL para el rastreador', '⬇ BL list for the tracker')}
              </a>
            </li>
            <li>
              {tr(
                'Cuando termine, elige todos los archivos de la carpeta tracking_pdfs (los PDF y, de Maersk, también los .json, que son más exactos; la app toma el más reciente de cada BL) y el Excel Shipment_output.xlsx.',
                'When it finishes, choose all the files in the tracking_pdfs folder (the PDFs and, for Maersk, also the .json files, which are more precise; the app takes the latest of each BL) and the Shipment_output.xlsx Excel.',
              )}
            </li>
            <li>
              {tr(
                'Revisa lo que cambia en cada exportación y guarda. Solo se tocan las que no están en Delivered, Imported/Exported, Unwind ni Canceled.',
                'Review what changes in each export and save. Only exports not in Delivered, Imported/Exported, Unwind or Canceled are touched.',
              )}
            </li>
          </ol>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          className="text-apagado hover:text-tinta"
          aria-label={tr('Cerrar', 'Close')}
        >
          ×
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <ElegirArchivo
          etiqueta={tr('PDF y Excel del rastreador', 'Tracker PDFs and Excel')}
          accept=".pdf,.json,.xlsx,.xlsm"
          multiple
          archivos={archivos.length > 6 ? [] : archivos}
          deshabilitado={cargar.isPending}
          onElegir={todos => {
            const {elegidos, descartados: d} = archivosDelRastreador(todos);
            setArchivos(elegidos);
            setDescartados(d);
            setResultado(null);
            if (elegidos.length) void enviar('revisar', elegidos);
          }}
        />
        {archivos.length > 6 && (
          <span className="text-apagado">
            {tr(
              `${numero(archivos.length)} archivos${descartados ? ` (se omitieron ${numero(descartados)} archivos más antiguos del mismo BL)` : ''}`,
              `${numero(archivos.length)} files${descartados ? ` (${numero(descartados)} older files of the same BL skipped)` : ''}`,
            )}
          </span>
        )}
        {cargar.isPending && <span className="text-apagado">{tr('Leyendo los archivos…', 'Reading the files…')}</span>}
      </div>
      {error && (
        <p className="text-alerta" role="alert">
          {error}
        </p>
      )}

      {r && (
        <div className="space-y-3">
          <p
            role="status"
            className={r.aplicado ? 'rounded-md bg-verde-claro/60 p-2 font-medium text-verde-oscuro' : ''}
          >
            {r.aplicado
              ? tr(
                  `Listo: se actualizaron ${numero(r.resumen.conCambios)} exportaciones (${numero(r.resumen.cambiosDeEstado)} con cambio de estado). Cada PDF quedó en el expediente de su exportación.`,
                  `Done: ${numero(r.resumen.conCambios)} exports updated (${numero(r.resumen.cambiosDeEstado)} with a status change). Each PDF was added to its export's file.`,
                )
              : tr(
                  `Revisión (todavía no se guardó nada): ${numero(r.resumen.conCambios)} exportaciones con cambios, ${numero(r.resumen.sinCambios)} sin cambios, ${numero(r.omitidas.length)} en estado final (no se tocan).`,
                  `Review (nothing saved yet): ${numero(r.resumen.conCambios)} exports with changes, ${numero(r.resumen.sinCambios)} unchanged, ${numero(r.omitidas.length)} in a final status (not touched).`,
                )}
          </p>
          {!r.aplicado && porGuardar > 0 && (
            <button
              type="button"
              disabled={cargar.isPending}
              onClick={() => void enviar('aplicar')}
              className="rounded-md bg-verde px-3 py-1.5 font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {tr(
                `Guardar los cambios de ${numero(porGuardar)} exportaciones`,
                `Save the changes of ${numero(porGuardar)} exports`,
              )}
            </button>
          )}
          <div className="max-h-[28rem] overflow-auto rounded-md border border-linea">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                  <th className="px-2 py-1 font-semibold">{tr('Exportación', 'Export')}</th>
                  <th className="px-2 py-1 font-semibold">BL</th>
                  <th className="px-2 py-1 font-semibold">{tr('Estado', 'Status')}</th>
                  <th className="px-2 py-1 font-semibold">{tr('Qué cambia', 'What changes')}</th>
                  <th className="px-2 py-1 font-semibold">{tr('Último evento real', 'Latest actual event')}</th>
                </tr>
              </thead>
              <tbody>
                {r.exportaciones.map(x => (
                  <tr key={x.codigo} className="border-t border-linea/70 align-top">
                    <td className="px-2 py-1">
                      <Link
                        to={`/seguimiento/${encodeURIComponent(x.codigo)}`}
                        className="font-medium text-verde hover:underline"
                      >
                        {x.codigo}
                      </Link>
                    </td>
                    <td className="px-2 py-1 text-xs">
                      {x.bl}
                      <div className="text-apagado">
                        {x.naviera ?? ''} {x.fechaConsulta ? `· ${fecha(x.fechaConsulta)}` : ''}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      {x.estadoNuevo ? (
                        <span>
                          {x.estadoActual} → <b className="text-verde-oscuro">{x.estadoNuevo}</b>
                        </span>
                      ) : (
                        x.estadoActual
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {x.cambios.length === 0 && !x.estadoNuevo && (
                        <span className="text-apagado">{tr('Sin cambios', 'No changes')}</span>
                      )}
                      {x.cambios.map(c => (
                        <div key={c.campo} className="text-xs">
                          <span className="text-apagado">{c.etiqueta}:</span> {c.antes ?? '—'} →{' '}
                          <b>{c.despues ?? '—'}</b>
                        </div>
                      ))}
                      {x.avisos.map(a => (
                        <div key={a} className="text-xs text-alerta">
                          ⚠ {a}
                        </div>
                      ))}
                    </td>
                    <td className="px-2 py-1 text-xs text-apagado">{x.ultimoEvento ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.sinExportacion.length > 0 && (
            <details>
              <summary className="cursor-pointer text-apagado">
                {tr(
                  `${numero(r.sinExportacion.length)} BL sin exportación en la app`,
                  `${numero(r.sinExportacion.length)} BLs without an export in the app`,
                )}
              </summary>
              <p className="mt-1 text-xs text-apagado">
                {r.sinExportacion.map(s => `${s.bl}${s.codigoDelExcel ? ` (${s.codigoDelExcel})` : ''}`).join(', ')}
              </p>
              <p className="text-xs text-apagado">
                {tr(
                  'Registra el BL o el booking en la exportación (o crea la exportación) y vuelve a subir.',
                  'Record the BL or booking on the export (or create the export) and upload again.',
                )}
              </p>
            </details>
          )}
          {r.sinInformacion.length > 0 && (
            <details>
              <summary className="cursor-pointer text-apagado">
                {tr(
                  `${numero(r.sinInformacion.length)} PDF sin información`,
                  `${numero(r.sinInformacion.length)} PDFs without information`,
                )}
              </summary>
              <ul className="mt-1 list-disc pl-5 text-xs text-apagado">
                {r.sinInformacion.map(s => (
                  <li key={s.archivo}>
                    {s.archivo}: {s.motivo}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {r.omitidas.length > 0 && (
            <details>
              <summary className="cursor-pointer text-apagado">
                {tr(
                  `${numero(r.omitidas.length)} exportaciones en estado final (no se tocan)`,
                  `${numero(r.omitidas.length)} exports in a final status (not touched)`,
                )}
              </summary>
              <p className="mt-1 text-xs text-apagado">{r.omitidas.map(o => `${o.codigo} (${o.estado})`).join(', ')}</p>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
